import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import FullPageLoader from "@Components/FullPageLoader";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import config from "@Config/index";
import FilterIcon from "@Icons/FilterIcon";
import {
  applyFilter,
  setFilterUniversityList,
  updateDynamicFilterCountData,
} from "@Pages/Search/data/searchPageReducer";
import { convertToFilterObj } from "@Pages/Search/utils/formatFilterState";
// import getAllFilterCount from "@Pages/Search/utils/getAllFilterCount";
import getFilterAction from "@Pages/Search/utils/getFilterAction";
import getFilterActiveStatus from "@Pages/Search/utils/getFilterActiveStatus";
import updateFilterValueByKey from "@Pages/Search/utils/updateFilterValueByKey";
import { AppState } from "@Store/index";
import axios from "axios";
import { COUNTRIES_WITH_MONTHLY_DURATION, PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { Suspense, lazy, useEffect, useReducer, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import Badge from "./components/Badge/Badge";
import BottomOverlayContainer from "./components/BottomOverlayContainer/BottomOverlayContainer";
import FiltersBadgeGroup from "./components/FiltersBadgeGroup/FiltersBadgeGroup";
import filtersModalReducer from "./components/FiltersModal/filtersModalReducer";
import fetchUniversityData from "./data/fetchUniversityData";
import {
  MAIN_FILTERS_WITH_SORT,
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
} from "./data/filtersList";
import styles from "./filterSection.module.scss";
import { ActiveFilterType } from "./types";
import FiltersBadgeGroupDesktop from "./components/FiltersBadgeGroup/FiltersBadgeGroupDesktop";
import Button from "@Components/Button";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import useIsDesktop from "hooks/useIsDesktop";
import FilterIconV2 from "@Icons/FilterIconV2";
import getAllFilterCount from "@Pages/Search/utils/getAllFilterCount";
import { SmartFilterPanel } from "./components/SmartFilters";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { logAppEvent } from "@Utils/appAnalytics";
import {
  APP_EVENTS,
  APP_PAGE,
  FILTER_FLOW,
  FILTER_MODE,
  filterTypeMap,
} from "@Utils/webApputils/analyticEvents";
import getCookie from "helpers/getCookie";
import useHousingFestFilter from "@Pages/Search/hooks/useHousingFestFilter";

const ActiveFilterComponent = lazy(
  () => import("./components/ActiveFilterComponent/ActiveFilterComponent"),
);
const FiltersModal = lazy(() => import("./components/FilterModalV2/FilterModalV2"));

type Props = {
  isDesktop?: boolean;
  showOnlyFilterIcon?: boolean;
};

const FilterSection = ({ isDesktop = false, showOnlyFilterIcon = false }: Props) => {
  const { dynamicFilterCountData, filters, region, readyStatus, smartFilterIsOpen } = useSelector(
    ({ searchPageData, smartFilterData }: AppState) => ({
      dynamicFilterCountData: searchPageData.dynamicFilterCountData,
      filters: searchPageData.filters,
      region: searchPageData.region,
      readyStatus: searchPageData.readyStatus,
      smartFilterIsOpen: smartFilterData?.isOpen || false,
    }),
    shallowEqual,
  );
  const isFirstTime = useRef(true);
  const isDesktopDevice = useIsDesktop();
  const {
    isWhitelabel,
    partner_id,
    isUniversityPartner,
    citiesList,
    isRestrictedSearchEnabled,
    whitelabelConfig,
  } = useWhitelabel();
  const [activeFilter, setActiveFilter] = useState<ActiveFilterType>("");
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const { toggleFilter: toggleHousingFest } = useHousingFestFilter();
  const [universityData, setUniversityData] = useState<{
    data: { inventoryCount: any; label: string; value: string }[];
    ancestorId: string;
    isLoading: boolean;
  }>({
    data: [],
    ancestorId: "",
    isLoading: true,
  });
  const [localityData, setLocalityData] = useState<{
    data: { inventoryCount: any; label: string; value: string }[];
    ancestorId: string;
    isLoading: boolean;
  }>({
    data: [],
    ancestorId: "",
    isLoading: true,
  });
  const ancestorId =
    region?.region_type === "establishment" || region?.region_type === "sublocality"
      ? region?.parent_id
      : region?.id;
  const scrollRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const hasFilterInteracted = useRef(false);
  const lastFetchedParams = useRef<string | null>(null);
  const searchListDispatch = useDispatch();
  const isSmartFiltersEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SMART_FILTERS) && !config.IS_CHINA_APP;
  const [currFilters, dispatch] = useReducer(filtersModalReducer, filters);

  useEffect(() => {
    let isMounted = true;
    const checkOverflow = () => {
      if (isMounted && containerRef.current) {
        const hasOverflow = containerRef.current.scrollWidth - containerRef.current.clientWidth > 5;
        setIsOverflowing(hasOverflow);
      }
    };

    checkOverflow();

    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.ready.then(() => {
        if (isMounted) {
          checkOverflow();
        }
      });
    }

    if (typeof window === "undefined") {
      return () => {
        isMounted = false;
      };
    }

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        checkOverflow();
      });
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
      if (scrollRef.current) {
        observer.observe(scrollRef.current);
      }
      return () => {
        isMounted = false;
        observer.disconnect();
      };
    }

    window.addEventListener("resize", checkOverflow);
    return () => {
      isMounted = false;
      window.removeEventListener("resize", checkOverflow);
    };
  }, [currFilters]);

  useEffect(() => {
    dispatch({ type: "applyAll", payload: filters });
    lastFetchedParams.current = null;
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    const fetchCountData = async () => {
      // const hasLocalStorage = typeof localStorage !== "undefined";
      // const storedBudget = hasLocalStorage ? localStorage.getItem("budget_step") : null;
      // const localStorageStep = storedBudget ? JSON.parse(storedBudget) : null;

      const isPricingStepCountFirstTime = false;
      // isFirstTime.current ||
      // !localStorageStep?.name ||
      // localStorageStep?.name !== region?.canonical_name;

      const getFilteredValues = (list: any, selected: any) =>
        list
          .map((it: any) => it.value)
          .filter((it: any) => selected?.includes(it))
          .join(",");
      const specificRoomTypes = getFilteredValues(ROOM_TYPE_SPECIFIC.list, currFilters.roomType);

      const fallbackRoomTypes = getFilteredValues(ROOM_TYPE_FILTER.list, currFilters.roomType);

      const filterParam = {
        ...convertToFilterObj(currFilters),

        room_types: specificRoomTypes || fallbackRoomTypes,

        bathroom_types: getFilteredValues(SHARING_OPTIONS_BATHROOM.list, currFilters.roomType),

        kitchen_types: getFilteredValues(SHARING_OPTIONS_KITCHEN.list, currFilters.roomType),
      };

      if (filterParam["meta_types"]) {
        delete filterParam["meta_types"];
      }
      if (!filterParam["available"]) {
        delete filterParam["available"];
      }

      const finalFilter = Object.keys(filterParam).reduce((acc: any, it) => {
        if (filterParam[it]) {
          acc[it] = filterParam[it];
        }
        return acc;
      }, {});

      const params = {
        status: "active",
        agg_keys: [
          "room_types",
          "bathroom_types",
          "kitchen_types",
          "lease_duration",
          "lease_duration_monthly",
          "meta_unit_types",
          "available",
          "price_stats",
          "flexible_stay_duration",
          "guarantor",
          isPricingStepCountFirstTime ? "pricing_step_count" : "",
          "move_in_month",
          "flexible_move_in",
          "amenities_grouped",
          "bills_included",
        ].join(","),
        ...(currFilters.locality &&
          currFilters.university && {
            locality_canonical_name: filters.locality,
            region_canonical_name: currFilters.university,
            locality_radius: 10000,
          }),
        ...(currFilters.locality
          ? {
              locality_canonical_name: currFilters.locality || region?.canonical_name,
            }
          : {
              region_canonical_name: currFilters.university || region?.canonical_name,
            }),
        ...finalFilter,
        ...(finalFilter?.range_available_price && {
          ...(COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)
            ? { range_available_price: finalFilter?.range_available_price }
            : { weekly_range_price: finalFilter?.range_available_price }),
          duration: COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)
            ? "monthly"
            : "weekly",
        }),
        region_type: "circle",
        region_radius:
          finalFilter.region_radius ||
          (region?.location?.country?.short_name === "AE" ? 60000 : 20000),
        use_v2_structure: true,
        // ...(!isPricingStepCountFirstTime && {
        //   agg_key: "pricing_step_count",
        //   agg_value: localStorageStep?.step_count || 10,
        // }),
      };

      if ((isWhitelabel && isUniversityPartner) || isRestrictedSearchEnabled) {
        params.whitelabel_partner_id = partner_id;
      }

      // Add nearby params to filter count when on nearby page
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        const localityParam = urlParams.get("locality");
        // Check if locality includes "near-by" to match the region name pattern
        if (localityParam && localityParam.includes("near-by")) {
          const nearbyLat = getCookie("nearby_lat");
          const nearbyLng = getCookie("nearby_lng");
          if (nearbyLat && nearbyLng) {
            params.isNearbyPage = true;
            params.lat = parseFloat(nearbyLat);
            params.lng = parseFloat(nearbyLng);
            params.region_radius = 30000;
          }
        }
      }

      delete params["range_available_price"];

      // For monthly countries, re-add range_available_price with duration
      // (the delete above removes the raw value from finalFilter; the conditional spread
      // needs to be applied after the delete to keep the correct key)
      if (
        finalFilter?.range_available_price &&
        COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)
      ) {
        params.range_available_price = finalFilter.range_available_price;
        params.duration = "monthly";
      }

      // if (params?.tags?.includes("dual_occupancy")) {
      //   params.meta_dual_occupancy = true;
      // }

      // Special handling for the "near-by-{region-name}" locality search to use region-level counting
      // Check if locality includes "near-by" to match the region name pattern
      if (params.locality && String(params.locality).includes("near-by")) {
        delete params.lat;
        delete params.lng;
        delete params.isNearbyPage;
        delete params.locality;
        delete params.locality_canonical_name;
        params.region_canonical_name = region?.canonical_name;
      }

      if (
        (params.region_canonical_name || params.locality_canonical_name || params.isNearbyPage) &&
        JSON.stringify(params) !== JSON.stringify(dynamicFilterCountData.params) &&
        JSON.stringify(params) !== lastFetchedParams.current
      ) {
        lastFetchedParams.current = JSON.stringify(params);
        try {
          const response = await axios(
            // Note: This api needs to be updated to fix the count for whitelabel
            `${config.API_URL}/api/v1/inventories/inventory_filter_count`,
            {
              params: {
                ...params,
                use_java_api: true,
              },
            },
          );

          const data = response?.data?.data;
          if (data?.data) {
            data.data.meta_types = {
              ...data.data.room_types,
              ...data.data.kitchen_types,
              ...data.data.bathroom_types,
              ...data.data.pricing_step_count,
            };
          }
          isFirstTime.current = false;
          searchListDispatch(
            updateDynamicFilterCountData({
              isLoading: false,
              data,
              params,
            }),
          );
        } catch (error) {
          console.error("Failed to fetch filter counts:", error);
          lastFetchedParams.current = null;
          searchListDispatch(
            updateDynamicFilterCountData({
              isLoading: false,
              data: null,
              params,
            }),
          );
        }
      }
    };
    // On desktop, only fetch filter counts when user has opened a filter or the modal.
    if (
      isDesktopDevice &&
      !showFiltersModal &&
      activeFilter === "" &&
      !smartFilterIsOpen &&
      !hasFilterInteracted.current
    ) {
      return;
    }
    fetchCountData();
  }, [JSON.stringify(currFilters), showFiltersModal, activeFilter, readyStatus, smartFilterIsOpen]);

  useEffect(() => {
    if (readyStatus === "request") {
      searchListDispatch(
        updateDynamicFilterCountData({
          data: null,
          isLoading: true,
          params: null,
        }),
      );
    }
  }, [readyStatus]);

  useEffect(() => {
    if (
      universityData.ancestorId !== ancestorId &&
      (activeFilter === "University" || showFiltersModal)
    ) {
      const getData = async () => {
        let data = [];
        if (isWhitelabel && isUniversityPartner) {
          const currentCity = citiesList?.find(
            (city) => city.canonical_name === region.canonical_name,
          );
          data = currentCity?.campuses?.reduce((acc, campus) => {
            if (campus?.parent_id === ancestorId) {
              acc.push({
                label: campus.name,
                value: campus.canonical_name,
              });
            }
            return acc;
          }, [] as any);
        } else {
          data = await fetchUniversityData({ ancestorId, isWhitelabel, partner_id });
        }
        const finalData = {
          data,
          ancestorId,
          isLoading: false,
        };
        setUniversityData(finalData);
        searchListDispatch(setFilterUniversityList(finalData));
      };
      getData();
    }
    if (
      localityData.ancestorId !== ancestorId &&
      (activeFilter === "Locality" || showFiltersModal)
    ) {
      const getData = async () => {
        const data = await fetchUniversityData({
          ancestorId,
          regionType: "sublocality",
          isWhitelabel,
          partner_id,
        });
        setLocalityData({
          data,
          ancestorId,
          isLoading: false,
        });
      };
      getData();
    }
  }, [ancestorId, activeFilter, showFiltersModal]);

  const hideBottomSheetOverlay = () => {
    setActiveFilter("");
  };

  const handleFilterLabelClick = (name: ActiveFilterType = "") => {
    hasFilterInteracted.current = true;

    // Housing Fest is a direct toggle filter, no bottom sheet needed
    if (name === "Housing Fest") {
      toggleHousingFest();
      return;
    }

    setActiveFilter(name);
    const gaEvent = getFilterAction(name);
    logAppEvent({
      eventName: APP_EVENTS.FILTER_CTA_CLICKED,
      payload: {
        filter_name: name,
        filter_flow: FILTER_FLOW.QUICK_FILTER,
        screen_name: APP_PAGE.SEARCH,
      },
    });
    gtagEventEmitter({ action: gaEvent, category: PAGE.SEARCH });
  };

  const openFiltersModal = () => {
    hasFilterInteracted.current = true;
    setShowFiltersModal(true);
    gtagEventEmitter({ action: "more_filters_clicked", category: PAGE.SEARCH });
  };

  useEffect(() => {
    window.addEventListener("OPEN_FILTER_MODAL", openFiltersModal);

    return () => {
      window.removeEventListener("OPEN_FILTER_MODAL", openFiltersModal);
    };
  }, []);

  // Close bottom sheet when browser back/forward navigation occurs
  useEffect(() => {
    const handleCloseBottomSheet = () => {
      setActiveFilter("");
    };

    window.addEventListener("CLOSE_FILTER_BOTTOM_SHEET", handleCloseBottomSheet);

    return () => {
      window.removeEventListener("CLOSE_FILTER_BOTTOM_SHEET", handleCloseBottomSheet);
    };
  }, []);

  const closeFiltersModal = (isClear = false) => {
    setShowFiltersModal(false);
    if (scrollRef.current) {
      scrollRef.current?.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
    if (!isClear) {
      // Reset filter state back to original filter state if modal is closed at it and filters are not applied
      dispatch({ type: "applyAll", payload: filters });
    }
  };

  const handleApplyFilters = () => {
    const filterType = filterTypeMap[activeFilter];
    let filterValue;
    if (filterType === "sortBy") {
      filterValue = { [filterType]: currFilters.sortKey };
    } else if (filterType === "stayDuration") {
      filterValue = { [filterType]: [currFilters.minLeaseDuration, currFilters.maxLeaseDuration] };
    } else {
      filterValue = { [filterType]: (currFilters as Record<string, any>)[filterType] };
    }

    logAppEvent({
      eventName: APP_EVENTS.FILTER_APPLIED,
      payload: {
        filter_flow: FILTER_FLOW.QUICK_FILTER,
        mode: FILTER_MODE.LISTING_VIEW,
        screenName: APP_PAGE.SEARCH,
        filterType,
        filterValue,
      },
    });

    searchListDispatch(applyFilter(currFilters));
    hideBottomSheetOverlay();
    if (scrollRef.current) {
      scrollRef.current?.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const handleClearClick = () => {
    const key = MAIN_FILTERS_WITH_SORT.find((filter: any) => filter.name === activeFilter)?.key;

    if (key) {
      let updatedState = updateFilterValueByKey(key, "", currFilters);
      // Preserve locality=near-by-{region-name} when clearing individual filters
      // Check if locality includes "near-by" to match the region name pattern
      if (
        filters.locality &&
        String(filters.locality).includes("near-by") &&
        !updatedState.locality
      ) {
        updatedState = { ...updatedState, locality: filters.locality };
      }
      dispatch({ type: "applyAll", payload: updatedState });
      searchListDispatch(applyFilter(updatedState, key === "university", key === "locality"));
    }
    if (scrollRef.current) {
      scrollRef.current?.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("filtered_university_and_region");
    }
    hideBottomSheetOverlay();
  };

  const onResetClick = (name: string) => {
    const key = MAIN_FILTERS_WITH_SORT.find((filter: any) => filter.name === name)?.key;
    if (key) {
      let updatedState = updateFilterValueByKey(key, "", currFilters);
      // Preserve locality=near-by-{region-name} when resetting individual filters
      // Check if locality includes "near-by" to match the region name pattern
      if (
        filters.locality &&
        String(filters.locality).includes("near-by") &&
        !updatedState.locality
      ) {
        updatedState = { ...updatedState, locality: filters.locality };
      }
      dispatch({ type: "applyAll", payload: updatedState });
      searchListDispatch(applyFilter(updatedState, key === "university", key === "locality"));
      setActiveFilter("");
    }
  };

  const hideBottomSheetOverlayWithFilterApply = () => {
    dispatch({ type: "applyAll", payload: filters });
    hideBottomSheetOverlay();
  };

  const filterCount = getAllFilterCount(filters);

  const { t } = useTranslationHook();

  return (
    <>
      {showOnlyFilterIcon ? (
        <div
          className={formatClassNames(styles.iconWrap, filterCount > 0 && styles.active)}
          role="none"
          onClick={openFiltersModal}
        >
          {filterCount > 0 && <div className={styles.count}>{filterCount}</div>}
          <FilterIconV2 />
        </div>
      ) : (
        <div
          ref={containerRef}
          className={formatClassNames(
            styles.container,
            isWhitelabel &&
              styles[
                whitelabelConfig?.is_custom_link_enabled
                  ? "whitelabelWithCustomLinks"
                  : "whitelabel"
              ],
          )}
        >
          <div className={styles.filterList} ref={scrollRef}>
            {isDesktop ? (
              <FiltersBadgeGroupDesktop
                filtersState={currFilters}
                dispatch={dispatch}
                universityData={universityData}
                localityData={localityData}
                onFilterClick={handleFilterLabelClick}
                onApplyClick={(payload: any) => {
                  searchListDispatch(applyFilter({ ...currFilters, ...payload }));
                }}
                activeFilter={activeFilter}
                onResetClick={onResetClick}
                handleOnClose={() => setActiveFilter("")}
                dynamiceFilterCount={dynamicFilterCountData.data}
                openFiltersModal={openFiltersModal}
                filterCount={filterCount}
              />
            ) : (
              <FiltersBadgeGroup
                onFilterClick={handleFilterLabelClick}
                filtersState={currFilters}
                dynamiceFilterCount={dynamicFilterCountData.data}
              />
            )}
            {!isDesktop && (
              <Badge
                value={
                  isWhitelabel
                    ? t("searchpage.topsearchsection.filtersection.advancedfiltersbutton")
                    : t("searchpage.topsearchsection.filtersection.filterbutton")
                }
                onClick={openFiltersModal}
                icon={FilterIcon}
                isActive={getFilterActiveStatus("Filters", filters)}
                count={filterCount}
                dataTestId="filters-button"
              />
            )}
            {isDesktop && (
              <div
                className={formatClassNames(
                  styles.clearAllBtnWrap,
                  isOverflowing && styles.isOverflowing,
                )}
              >
                <Button
                  variant="subtle"
                  greyDisabled={
                    currFilters.sortKey === "relevance_sorting_v2,desc" &&
                    Object.keys(currFilters).filter(
                      (key) => key !== "sortKey" && key !== "locality",
                    ).length === 0
                  }
                  onClick={() => {
                    // Preserve locality=near-by-{timestamp} when clearing filters
                    // Check if locality includes "near-by" to match the timestamp pattern
                    const preservedFilters =
                      filters.locality && String(filters.locality).includes("near-by")
                        ? { locality: filters.locality }
                        : {};
                    dispatch({ type: "reset", payload: preservedFilters });
                    searchListDispatch(applyFilter(preservedFilters));
                    gtagEventEmitter({
                      action: "clear_all_filters_clicked",
                      category: PAGE.SEARCH,
                    });
                  }}
                  className={styles.clearAllBtn}
                  dataTestId="filter-clearAll-button"
                >
                  {t("searchpage.topsearchsection.filtersection.clearallbutton")}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Smart Filter Panel */}
      {isSmartFiltersEnabled && <SmartFilterPanel />}
      {/* Bottom Overlays */}
      {!isDesktop && activeFilter !== "" && (
        <Portal rootId="forPortal">
          <BottomSheetOverlay
            linePosition="bottom"
            hideBottomSheetOverlay={hideBottomSheetOverlayWithFilterApply}
            isHistoryPush
            name={activeFilter}
          >
            <Suspense fallback={<FullPageLoader height="230px" width="360px" />}>
              <BottomOverlayContainer
                isClearActive={
                  activeFilter === "Housing Type"
                    ? true
                    : getFilterActiveStatus(activeFilter, currFilters, false, true)
                }
                onApplyClick={handleApplyFilters}
                onClearClick={handleClearClick}
                count={dynamicFilterCountData?.data?.meta?.count}
              >
                <ActiveFilterComponent
                  filterName={activeFilter}
                  filtersState={currFilters}
                  dispatch={dispatch}
                  universityData={universityData}
                  localityData={localityData}
                  dynamiceFilterCount={dynamicFilterCountData.data}
                  dynamicFilterIsLoading={
                    dynamicFilterCountData.isLoading || readyStatus === "request"
                  }
                  showCount
                  withCross
                  hideBottomSheetOverlay={hideBottomSheetOverlayWithFilterApply}
                  openingSource="searchFilterMobile"
                />
              </BottomOverlayContainer>
            </Suspense>
          </BottomSheetOverlay>
        </Portal>
      )}
      {/* Filters Modal */}
      {showFiltersModal && (
        <Portal rootId="forPortal">
          <Modal
            hideModal={() => closeFiltersModal()}
            size={isDesktop ? "large" : "fullScreen"}
            {...(isDesktop ? { align: "center", className: styles.desktopModalContainer } : {})}
            animation={isDesktop ? "opacity" : "bottomToTop"}
            isHistoryPush={!isDesktop}
            type="FiltersModal"
          >
            <Suspense fallback={<FullPageLoader />}>
              <FiltersModal
                onClose={closeFiltersModal}
                modalFilters={currFilters}
                dispatch={dispatch}
                universityData={universityData}
                localityData={localityData}
                dynamiceFilterCount={dynamicFilterCountData.data}
                dynamicFilterIsLoading={
                  dynamicFilterCountData.isLoading || readyStatus === "request"
                }
                region={region}
                showCount
                withResetButton={isDesktop}
                onResetClick={onResetClick}
              />
            </Suspense>
          </Modal>
        </Portal>
      )}
    </>
  );
};

export default wrapperHOC(FilterSection, {
  componentName: "FilterSection-SearchPage",
  showForChina: true,
});
